import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  CircularProgress,
  Container,
  Badge,
  Avatar,
} from '@mui/material';
import {
  School as SchoolIcon,
  Support as SupportIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from './auth/AuthContext';
import api from '../utils/api';
import AOS from 'aos';
import 'aos/dist/aos.css';

const AdminSupportDashboard = () => {
  const navigate = useNavigate();
  const { isAdminAuthenticated, logout } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 800, easing: 'ease-in-out', once: true });
  }, []);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!isAdminAuthenticated) {
        throw new Error('Please login as admin first');
      }

      const response = await api.get('/api/admin/support/admin/conversations');

      if (response.data && Array.isArray(response.data.conversations)) {
        setConversations(response.data.conversations);
      } else {
        throw new Error('Invalid data format from server');
      }
    } catch (err) {
      console.error('Support conversations error:', err);
      setError(
        err.response?.data?.message || err.message || 'Failed to load conversations'
      );
      if (err.response?.status === 401) {
        logout();
        navigate('/government-login');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, [isAdminAuthenticated, logout, navigate]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchConversations();
  };

  const renderConversationItem = (item) => (
    <Grid item xs={12} key={item._id}>
      <Card
        sx={{
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          borderRadius: 2,
          mb: 2,
          bgcolor: '#fff',
        }}
        data-aos="fade-up"
      >
        <CardActionArea
          onClick={() =>
            navigate('/admin-support-chat', {
              state: {
                conversationId: item._id,
                schoolId: item.schoolId,
                schoolName: item.schoolName || 'School',
                schoolAddress: item.schoolAddress || 'Address not available',
              },
            })
          }
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: '#6200ee', mr: 2 }}>
              <SchoolIcon sx={{ color: '#fff' }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 0.5 }}>
                {item.schoolName || 'School'}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#666', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {item.schoolAddress || 'Address not available'}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#666', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {item.lastMessage || 'No messages yet'}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              {item.unreadCount > 0 && (
                <Badge
                  badgeContent={item.unreadCount}
                  color="primary"
                  sx={{ mb: 1, '.MuiBadge-badge': { bgcolor: '#6200ee', color: '#fff' } }}
                />
              )}
              <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                {item.lastMessageAt
                  ? new Date(item.lastMessageAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : ''}
              </Typography>
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );

  if (loading && !refreshing) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#6200ee' }} />
        <Typography variant="h6" sx={{ mt: 3, color: '#2c3e50' }}>
          Loading support conversations...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
        }}
      >
        <Typography variant="h6" color="error" sx={{ mb: 3, textAlign: 'center' }}>
          {error}
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            if (!isAdminAuthenticated) {
              navigate('/government-login');
            } else {
              setLoading(true);
              setError(null);
              fetchConversations();
            }
          }}
          sx={{ bgcolor: '#6200ee', '&:hover': { bgcolor: '#7f39fb' }, px: 4, py: 1.5 }}
        >
          {isAdminAuthenticated ? 'Try Again' : 'Go to Login'}
        </Button>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }} data-aos="fade-down">
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#6200ee' }}>
          Support Conversations
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
          sx={{ borderColor: '#6200ee', color: '#6200ee' }}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>
      {conversations.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 5,
          }}
          data-aos="fade-up"
        >
          <SupportIcon sx={{ fontSize: 50, color: '#6200ee', mb: 2 }} />
          <Typography sx={{ color: '#666', mb: 3, textAlign: 'center' }}>
            No support conversations yet
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            sx={{ borderColor: '#6200ee', color: '#6200ee' }}
          >
            Refresh
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2} data-aos="fade-up">
          {conversations.map((item) => renderConversationItem(item))}
        </Grid>
      )}
    </Container>
  );
};

export default AdminSupportDashboard;